<template>
    <LayoutPage v-if="player.uuid">
        <template #header>
            <PlayerProjectHeader
                :actions="actions"
                :shareLinks="shareLinks"
                @publish="handlePublish"
            />
        </template>

        <template #sidebar>
            <router-view />
        </template>

        <template #preview>
            <PlayerProjectPreview />
        </template>

        <template #footer>
            <PlayerProjectFooter :actions="actions" @publish="handlePublish" />
        </template>
    </LayoutPage>
    <!-- Onboarding  -->
    <PlayerCreator v-if="isCreatorShown" />
    <!-- First steps Video -->
    <cHelpVideosAfterOnboarding v-else />
    <cModalUrl
        v-model:open="showUrlModal"
        v-model:slug="slug"
        :links="playerLinks"
        @updateUuid="changeUuid({ tool: 'player', uuid: player.uuid, newUuid: slug })"
    />
    <!-- Modal for renaming -->
    <ModalInput
        v-model:open="showRenameModal"
        v-model:text="projectName"
        :modal-data="modalRenameData"
        @cta="handleRenameProject(projectName)"
        @cancel="showRenameModal = false"
    />
    <cModalShare v-model:open="showModalShare" :links="shareLinks" />
</template>

<script>
import { markRaw } from "vue";
import { storeToRefs } from "pinia";
import { LayoutPage } from "@easy/ui/layouts";
import PlayerProjectHeader from "@/apps/player/project/PlayerProjectHeader.vue";
import PlayerProjectPreview from "@/apps/player/project/PlayerProjectPreview.vue";
import PlayerProjectFooter from "@/apps/player/project/PlayerProjectFooter.vue";
import PlayerCreator from "@/apps/player/project/onboarding/PlayerCreator.vue";
import { makeLogger } from "@/shared/consola";
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";
import {
    PencilLineIcon,
    ShareLinkCircleIcon,
    TrashBinIcon,
    LinkShareIcon,
    PlayerIcon,
    ShareLinkIcon,
    InvisibleIcon,
    GraphBarIncreaseIcon,
    AiIcon,
} from "@easy/ui/icons";
import cModalUrl from "@/shared/components/cModalUrl.vue";
import cModalShare from "@/shared/components/cModalShare.vue";
import { ModalInput } from "@easy/ui/modals";
import { EventBus } from "@/shared/projects/event-bus";
import { shareUrlService } from "@/shared/projects/share-url.service";
import { PLAYER_ROUTE_NAMES } from "@/apps/player/player.routes";
import { usePlayerCreatorStore } from "@/apps/player/project/onboarding/player-creator.store";
import cHelpVideosAfterOnboarding from "@/shared/components/help/cHelpVideosAfterOnboarding.vue";

const logger = makeLogger("PlayerProjectPage");

export default {
    components: {
        LayoutPage,
        ModalInput,
        PlayerCreator,
        PlayerProjectFooter,
        PlayerProjectHeader,
        PlayerProjectPreview,
        cHelpVideosAfterOnboarding,
        cModalShare,
        cModalUrl,
    },
    mounted() {
        this.$watch(() => this.$route.params.projectUuid, this.handleProjectChange, {
            immediate: true,
        });
        this.$watch(
            () => this.player.name,
            (playerName) => {
                this.projectName = playerName || "";
                document.title = `Easyplayer | ${playerName}`;
            },
            { immediate: true }
        );
        // FIXME: walk around for now when (publish)
        EventBus.$on("askForChanges", (resolve) => {
            resolve(false);
        });
    },
    setup() {
        const playerCreatorStore = usePlayerCreatorStore();
        const { isCreatorShown } = storeToRefs(playerCreatorStore);
        const { autoOpenCreator } = playerCreatorStore;
        return { isCreatorShown, autoOpenCreator };
    },
    data() {
        return {
            showModalShare: false,
            showUrlModal: false,
            showRenameModal: false,
            showShareModel: false,
            slug: this.$route.params.projectUuid,
            projectName: "",
        };
    },
    watch: {
        playerUuid: {
            handler(value) {
                if (!value) {
                    return;
                }
                this.autoOpenCreator(this.player);
            },
            immediate: true,
        },
    },
    computed: {
        ...mapState(["globalUrl", "previewLang"]),
        ...mapState("player", ["player"]),
        ...mapGetters(["isPublished", "appUrl"]),
        playerUuid() {
            return this.player.uuid;
        },
        actions() {
            return [
                {
                    label: this.$t("shared.live_preview"),
                    icon: markRaw(LinkShareIcon),
                    handler: () => {
                        window.open(this.playerURL, "_blank");
                    },
                    mobileOnly: true,
                },
                {
                    label: this.$t("shared.share"),
                    icon: markRaw(ShareLinkIcon),
                    handler: () => {
                        this.showModalShare = true;
                    },
                    mobileOnly: true,
                },
                {
                    label: this.$t("player.menu.panel"),
                    icon: markRaw(GraphBarIncreaseIcon),
                    handler: () =>
                        this.$router.push({
                            name: PLAYER_ROUTE_NAMES.panelUsers,
                            params: { projectUuid: this.player.uuid },
                        }),
                    mobileOnly: true,
                },
                {
                    label: this.$t("shared.generate_from_scratch"),
                    icon: markRaw(AiIcon),
                    handler: () => {
                        this.toggleCreator(true);
                    },
                },
                {
                    label: this.$t("shared.rename"),
                    icon: markRaw(PencilLineIcon),
                    handler: () => {
                        this.showRenameModal = true;
                    },
                },
                {
                    label: this.$t("shared.change_url.title"),
                    icon: markRaw(ShareLinkCircleIcon),
                    handler: () => {
                        this.showUrlModal = true;
                    },
                },
                this.isPublished(this.player)
                    ? {
                          label: this.$t("shared.unpublish"),
                          icon: markRaw(InvisibleIcon),
                          handler: () => {
                              this.unpublishPlayer();
                              logger.log("unpublish");
                          },
                      }
                    : {},
                { isDivider: true },
                {
                    label: this.$t("shared.delete"),
                    isDanger: true,
                    icon: markRaw(TrashBinIcon),
                    handler: this.askAndDeleteProject,
                },
            ];
        },
        playerLinks() {
            return [
                {
                    title: this.$t("player.urls.player"),
                    url: `${this.globalUrl}/players/<strong>${this.slug}</strong>`,
                },
                {
                    title: this.$t("player.urls.project"),
                    url: `${this.appUrl}/projects/<strong>${this.slug}</strong>`,
                },
            ];
        },
        playerURL() {
            const slug = this.player.uuid;
            return shareUrlService.makeUrlWithLang("players", slug, this.previewLang);
        },
        modalRenameData() {
            return {
                title: this.$t("shared.rename"),
                inputLabel: this.$t("shared.project_name"),
                ctaLabel: this.$t("shared.save"),
                cancelLabel: this.$t("shared.cancel"),
            };
        },
        shareLinks() {
            return [
                {
                    value: "player",
                    label: this.$t("player.urls.player"),
                    url: this.playerURL,
                    icon: PlayerIcon,
                    code: "",
                },
            ];
        },
    },
    methods: {
        ...mapActions("player", ["publishProject", "unpublishPlayer"]),
        ...mapActions("player/projects", ["loadProject", "saveSnapshot"]),
        ...mapActions("project", ["changeUuid", "removeProject", "renameProject"]),
        ...mapActions("modals", ["showConfirmModal"]),
        ...mapMutations(["showModal"]),
        handleProjectChange(uuid, oldUuid) {
            if (uuid && uuid !== oldUuid) {
                this.loadProject(uuid);
            }
        },
        async handleRenameProject(newName) {
            // FIXME There is problem with a SNAPSHOT.
            // When you rename project snapshot updates and overrides current data :(
            const error = await this.renameProject({
                tool: "player",
                project: this.player,
                newName,
            });
            if (!error) {
                this.showRenameModal = false;
            }
        },
        askAndDeleteProject() {
            const modalData = {
                type: "confirm",
                title: this.$t("shared.delete_project"),
                desc: this.$t("shared.delete_project_info"),
                actionBtn: this.$t("shared.delete"),
                cancelBtn: this.$t("shared.cancel"),
                callback: () => {
                    this.removeProject({ tool: "player", uuid: this.player.uuid });
                },
                size: "lg",
            };
            this.showModal(modalData);
        },
        handlePublish() {
            this.publishProject();
        },
    },
};
</script>
